// Terms of pronouns and possessives (Kielioppi § 4, 04-pronouns.mdx; lessons 6, 11, 18, 28).
// HV facts as 04-pronouns.mdx states them (wiki: High_Valyrian_Pronouns, Tips & Notes).
import { defineTerms } from './define';

export const terms = defineTerms({
  pronomini: {
    fi: {
      name: 'pronomini',
      short: '**Pronomini** eli asemosana korvaa substantiivin tai viittaa siihen, kuten *minä*, *tämä* tai *joku*.',
      compare: 'Sama sanaluokka kuin suomessa.',
    },
    en: {
      name: 'pronoun',
      short: 'A **pronoun** stands for a noun or points to one, such as *I*, *this* or *someone*.',
      compare: 'The same word class as in Finnish.',
    },
    related: ['persoonapronomini', 'demonstratiivi', 'epamaarainen-pronomini'],
    firstIn: 'personal',
  },
  persoona: {
    fi: {
      name: 'persoona',
      short:
        '**Persoona** kertoo, kuka puhuu tai kenestä puhutaan: 1. persoona on puhuja (*minä*, *me*), 2. puhuteltava (*sinä*, *te*) ja 3. joku muu (*hän*, *he*).',
      compare: 'Kuten suomessa.',
    },
    en: {
      name: 'person',
      short:
        '**Person** tells who speaks or is spoken of: the 1st person is the speaker (*I*, *we*), the 2nd the one addressed (*you*), the 3rd someone else (*he*, *she*, *they*).',
      compare: 'As in Finnish.',
    },
    related: ['persoonapronomini', 'persoonapaate'],
    firstIn: 'personal',
  },
  persoonapronomini: {
    fi: {
      name: 'persoonapronomini',
      short: '**Persoonapronomini** on sana *minä*, *sinä*, *hän*, *me*, *te* tai *he* tai sen vastine.',
      compare:
        'Valyriassa 3. persoonan pronomini valitaan sen sanan suvun mukaan, johon se viittaa, ei sen mukaan, onko kyse ihmisestä (*hän*) vai muusta (*se*).',
      long: 'Pronomini jää yleensä pois, koska verbin persoonapääte kertoo jo tekijän; valyria jättää sen pois myös 3. persoonassa. Pronominit taipuvat kaikissa sijoissa, mutta enimmäkseen arvaamattomasti.',
    },
    en: {
      name: 'personal pronoun',
      short: 'A **personal pronoun** is *I*, *you*, *he*, *she*, *we* or *they*, or its equivalent.',
      compare:
        'In High Valyrian the 3rd person pronoun follows the gender of the word it refers to, not whether a person (*hän*) or something else (*se*) is meant.',
      long: 'The pronoun is usually dropped, since the verb’s person ending already says who acts; High Valyrian drops it in the 3rd person too. Pronouns decline in every case, mostly unpredictably.',
    },
    related: ['persoona', 'persoonapaate', 'suku'],
    firstIn: 'personal',
  },
  refleksiivi: {
    fi: {
      name: 'refleksiivipronomini',
      short: '**Refleksiivipronomini** viittaa takaisin lauseen tekijään, kuten suomen *itse*: *rauhoita itsesi*.',
      compare: 'Suomi merkitsee *itse*-sanan persoonan omistusliitteellä (*itsesi*), valyria omalla sanallaan jokaisessa persoonassa.',
      long: 'Samalla sanalla korostetaan: ’sinä itse puhut kauniisti’. Monikon refleksiiveillä on myös merkitys ’toinen toisiaan’.',
    },
    en: {
      name: 'reflexive pronoun',
      short: 'A **reflexive pronoun** points back to the one doing the action, like *-self*: *calm yourself*.',
      compare: 'Finnish marks the person of *itse* with a possessive suffix (*itsesi*); High Valyrian has a separate word for each person.',
      long: 'The same word adds emphasis: ‘you yourself speak beautifully’. The plural reflexives can also mean ‘each other’.',
    },
    related: ['persoonapronomini', 'omistusliite'],
    firstIn: 'personal',
  },
  omistussana: {
    fi: {
      name: 'omistussana',
      short: '**Omistussana** kertoo omistajan: *minun*, *sinun*, *hänen*.',
      compare: 'Suomi merkitsee omistajan liitteellä itse substantiiviin (*talo|ni*); valyriassa omistussana on adjektiivi, joka mukautuu omistettuun sanaan.',
      long: 'Kolmannessa persoonassa sana mukautuu kahteen suuntaan: vartalo tulee omistajan suvusta, pääte omistetun sanan suvusta, luvusta ja sijasta.',
    },
    en: {
      name: 'possessive',
      short: 'A **possessive** tells the owner: *my*, *your*, *his*, *her*.',
      compare: 'Finnish puts a suffix on the noun itself (*talo|ni*); in High Valyrian the possessive is an adjective that agrees with the possessed noun.',
      long: 'In the 3rd person it agrees two ways: the stem comes from the owner’s gender, the ending from the possessed noun’s gender, number and case.',
    },
    related: ['omistuspronomini', 'omistusliite', 'adjektiivi', 'mukautuminen'],
    firstIn: 'possessives',
  },
  omistuspronomini: {
    fi: {
      name: 'omistuspronomini',
      short: '**Omistuspronomini** on omistussana, joka seisoo yksin ilman substantiivia: ’se on minun’.',
      compare: 'Suomessa kumpikin on *minun*; valyriassa ne ovat eri sanoja, ja pronomini taipuu kuin maasukuinen substantiivi.',
    },
    en: {
      name: 'possessive pronoun',
      short: 'A **possessive pronoun** is a possessive standing alone, without a noun: ‘it is mine’.',
      compare: 'Finnish says *minun* for both; High Valyrian keeps them apart, and the pronoun declines like a terrestrial noun.',
    },
    related: ['omistussana', 'maasuku'],
    firstIn: 'possessives',
  },
  demonstratiivi: {
    fi: {
      name: 'demonstratiivi',
      short: '**Demonstratiivi** eli osoittava pronomini osoittaa jotakin: *tämä*, *tuo*.',
      compare: 'Suomessa portaita on kolme (*tämä*, *tuo*, *se*), valyriassa kaksi, ja sana valitaan myös sen mukaan, onko osoitettava elollinen vai eloton.',
      long: 'Yksinään oleva demonstratiivi ei mukaudu substantiiviin. Substantiivin edellä käytetään sen adjektiivimuotoa, joka mukautuu kuten muutkin adjektiivit.',
    },
    en: {
      name: 'demonstrative',
      short: 'A **demonstrative** points at something: *this*, *that*.',
      compare: 'Finnish has three steps (*tämä*, *tuo*, *se*), High Valyrian two, and the word also depends on whether the thing is animate or inanimate.',
      long: 'A demonstrative standing alone does not agree with a noun. Before a noun its adjective form is used, which agrees like any adjective.',
    },
    related: ['elollisuus', 'pronomini', 'maarite'],
    firstIn: 'demonstratives',
  },
  'epamaarainen-pronomini': {
    fi: {
      name: 'epämääräinen pronomini',
      short: '**Epämääräinen pronomini** viittaa johonkin tarkemmin nimeämättä: *joku*, *jokin*, *ei kukaan*, *kaikki*.',
      compare: 'Kuten suomen *joku* ja *jokin*, valyriakin erottaa elollisen ja elottoman, myös sanassa ’kaikki’.',
    },
    en: {
      name: 'indefinite pronoun',
      short: 'An **indefinite pronoun** refers to someone or something without naming it: *someone*, *something*, *nobody*, *all*.',
      compare: 'Like Finnish *joku* and *jokin*, High Valyrian keeps animate and inanimate apart, even in ‘all’.',
    },
    related: ['pronomini', 'elollisuus', 'muutama', 'kollektiivi'],
    firstIn: 'indefinites',
  },
});
